class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestSequence = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('.predictive-search__results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.requestSequence++;
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Escape':
        this.abortController.abort();
        this.requestSequence++;
        this.close();
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const text = this.querySelector('[data-predictive-search-search-for-text]');
    if (text && previousTerm) text.textContent = text.textContent.split(previousTerm).join(newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    const sequence = ++this.requestSequence;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      const cachedMarkup = this.cachedResults[queryKey];
      this.renderSearchResults(cachedMarkup);
      if (window.PdilContents && !cachedMarkup.includes('pdil-search-contents')) {
        window.PdilContents.augment(cachedMarkup, searchTerm).then(augmented => {
          if (sequence !== this.requestSequence || searchTerm !== this.getQuery() || !this.isOpen || this.querySelector('[aria-selected="true"]')) return;
          this.cachedResults[queryKey] = augmented;
          this.renderSearchResults(augmented);
        }).catch(() => {});
      }
      return;
    }
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '5',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'hide',
      'resources[options][fields]': 'title,body,tag,product_type'
    });
    try {
      const response = await fetch(`${routes.predictive_search_url}?${params}`, {signal: this.abortController.signal});
      if (!response.ok) throw new Error('Search unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = document.querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Search unavailable');
      if (sequence !== this.requestSequence || searchTerm !== this.getQuery()) return;
      const markup = section.innerHTML;
      this.allPredictiveSearchInstances.forEach(instance => {instance.cachedResults[queryKey] = markup;});
      this.renderSearchResults(markup);
      if (window.PdilContents) {
        window.PdilContents.augment(markup, searchTerm).then(augmented => {
          if (sequence !== this.requestSequence || searchTerm !== this.getQuery() || !this.isOpen || this.querySelector('[aria-selected="true"]')) return;
          this.allPredictiveSearchInstances.forEach(instance => {instance.cachedResults[queryKey] = augmented;});
          this.renderSearchResults(augmented);
        }).catch(() => {});
      }
    } catch (error) {
      if (error.name === 'AbortError' || sequence !== this.requestSequence) return;
      this.close();
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const prefix = this.input.id || 'pdil-search';
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      ids.set(element.id, `${prefix}-${element.id}`);
      element.id = ids.get(element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => ids.get(id) || id).join(' '));
    });
    this.input.setAttribute('aria-controls', `${prefix}-predictive-search-results`);
    this.input.setAttribute('aria-owns', `${prefix}-predictive-search-results`);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

